/**
 * У який бік змінився масштаб — заради того, щоб рух це показав.
 *
 * Оболонка побудована на «гортанні сторінок, а не стеку екранів»
 * ([`docs/17` §6](../../../../docs/17-design-system.md)): рік, місяць,
 * тиждень і день — це не чотири різні розділи, а одна річ із різної
 * відстані. Крихти обіцяють саме це.
 *
 * Без руху обіцянка лишається словами: перехід виглядає як миттєва
 * підміна, і людина не бачить, вона пірнула чи виринула. Найпомітніше це
 * на «Сьогодні» з річного екрана — стрибок через три масштаби, після
 * якого доводиться перечитувати заголовок, щоб зрозуміти, де ти.
 */
import { SCALES, type HorizonKind } from "@beresta/core";
import { useRef } from "react";

/** `in` — углиб (рік → день), `out` — назовні, `null` — масштаб не мінявся. */
export type ZoomDirection = "in" | "out" | null;

export function zoomBetween(was: HorizonKind | null, now: HorizonKind): ZoomDirection {
  if (was === null || was === now) return null;
  // Порядок `SCALES` значущий і оголошений таким у `horizon/types.ts`:
  // від найширшого до найвужчого. Беремо його звідти, а не переписуємо
  // сюди список — інакше це другий опис того самого порядку.
  return SCALES.indexOf(now) > SCALES.indexOf(was) ? "in" : "out";
}

/**
 * Напрямок останньої зміни масштабу — і він **тримається**, доки масштаб
 * не зміниться знову.
 *
 * ⚠️ Перша редакція повертала напрямок лише на тому рендері, де масштаб
 * змінився, а далі — `null`. Виглядало правильно й ламалося тихо: цей
 * компонент перемальовується на кожну ревізію синхронізації, тож атрибут
 * зникав би посеред анімації, і рух обривався б на випадковому кадрі.
 * Помітити це в перевірці напрямку неможливо — вона дивиться на одну
 * зміну, а ламається друга.
 *
 * Обидва значення живуть у `ref`, а не в стані: від них нічого не
 * перемальовується, і зайвий прохід рендера коштував би того самого
 * кадру, який ми намагаємося зробити плавним.
 */
export function useZoomDirection(kind: HorizonKind | null): ZoomDirection {
  const was = useRef<HorizonKind | null>(null);
  const direction = useRef<ZoomDirection>(null);

  /*
   * ⚠️ Поза горизонтом — `null`, і памʼять хука **не чіпається**.
   *
   * Перша редакція брала тут запасний `"day"` і твердила в коментарі, що
   * це безпечно, бо напрямок від нього однаково не вживається. Неправда:
   * напрямок живе в `ref` і переживає демонтаж обгортки. Пішов із тижня
   * в пошук, повернувся на тиждень — і між ними в памʼяті стояв вигаданий
   * день, тобто екран «виринав» там, де масштаб не мінявся.
   */
  if (kind === null) return direction.current;

  if (was.current !== kind) {
    direction.current = zoomBetween(was.current, kind);
    was.current = kind;
  }

  return direction.current;
}
